import React, { useState } from 'react';
import {
  SlideOutPanel,
  SlideOutPanelBody,
  SlideOutPanelFooter,
  Button,
  Icon,
  Pagination,
} from '@repo/ui';
import { useBankTransactions } from '../queries';
import { numberUtil, dateUtil } from '@xtnd/web/shared/utils';

interface BankTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string | null;
  matterName?: string;
}

export function BankTransactionsModal({
  isOpen,
  onClose,
  matterId,
  matterName,
}: BankTransactionsModalProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 50;

  // Only call the API if matterId is a valid non-empty string that can be parsed as a number
  const isValidMatterId =
    matterId && matterId.trim() !== '' && matterId !== 'Unknown' && !isNaN(Number(matterId));

  const { data, isLoading, error } = useBankTransactions({
    matterId: matterId || '',
    page: currentPage,
    limit,
  });

  // Reset page when matter changes
  React.useEffect(() => {
    if (matterId) {
      setCurrentPage(1);
    }
  }, [matterId]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of modal content
    const modalBody = document.querySelector('[role="dialog"] [class*="overflow-y-auto"]');
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
  };

  return (
    <SlideOutPanel
      open={isOpen}
      onClose={onClose}
      width="xl"
      title="Bank Transactions"
      ariaLabel="Bank Transactions"
    >
      <div className="px-6 py-4 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600">
        <div className="flex items-center gap-2">
          <Icon name="HiCreditCard" className="h-5 w-5 text-xtnd-blue" />
          <div>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
              {matterName ? `Matter: ${matterName}` : `Matter ID: ${matterId}`}
            </p>
          </div>
        </div>
      </div>

      <SlideOutPanelBody className="flex-1 overflow-y-auto">
        {!isValidMatterId ? (
          <div className="text-center py-12">
            <Icon name="HiExclamationCircle" className="mx-auto h-12 w-12 text-yellow-500" />
            <h3 className="mt-2 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
              Invalid Matter ID
            </h3>
            <p className="mt-1 text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Cannot load bank transactions: Matter ID "{matterId}" is not valid.
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-xtnd-blue"></div>
            <p className="mt-4 text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
              Loading bank transactions...
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Icon name="HiExclamationCircle" className="mx-auto h-12 w-12 text-red-500" />
            <h3 className="mt-2 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
              Error loading bank transactions
            </h3>
            <p className="mt-1 text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
              {error instanceof Error ? error.message : 'Failed to load bank transactions'}
            </p>
          </div>
        ) : !data?.transactions || data.transactions.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="HiCreditCard" className="mx-auto h-12 w-12 text-xtnd-dark-300" />
            <h3 className="mt-2 text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50">
              No bank transactions found
            </h3>
            <p className="mt-1 text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
              There are no bank transactions available for this matter.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
              <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-xtnd-dark-800 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                {data.transactions.map(transaction => {
                  const amount = transaction.amount || 0;
                  const isPositive = amount >= 0;

                  return (
                    <tr
                      key={transaction.id}
                      className="hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
                        {transaction.date ? dateUtil.formatDate(transaction.date) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 max-w-xs truncate">
                        {transaction.description || 'No description'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                        {transaction.transaction_type || transaction.type || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                        <span
                          className={
                            isPositive
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }
                        >
                          {isPositive ? '+' : ''}
                          {numberUtil.formatCurrency(Math.abs(amount))}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SlideOutPanelBody>

      <SlideOutPanelFooter>
        <div className="flex justify-between items-center w-full">
          <div className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
            {isValidMatterId && data?.total
              ? `${data.total} transaction${data.total !== 1 ? 's' : ''} found`
              : ''}
          </div>
          <div className="flex gap-2 items-center">
            {isValidMatterId && data && data.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                totalRecords={data.total}
                hasNextPage={data.hasMore}
                hasPreviousPage={currentPage > 1}
                pageSize={limit}
                onPageChange={handlePageChange}
                onPageSizeChange={() => {}}
                showPageSizeSelector={false}
                showRecordCount={false}
              />
            )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </SlideOutPanelFooter>
    </SlideOutPanel>
  );
}
